/**
 * Local-only timing instrumentation for the AI Notetaker pipeline (browser).
 *
 * Enabled by: VITE_AI_NOTETAKER_TIMING=true
 *
 * Optional Medplum persistence is handled by aiNotetakerTimingMedplum.ts when enabled.
 * NEVER logs PHI (transcript text, note text, patient/provider names).
 */

import { formatConsoleLine, roundDurationMs } from './aiNotetakerTimingFormat';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type AiNotetakerTimingPhase = 'ai_generation';

export type AiNotetakerTimingEvent = {
  traceId: string;
  phase: AiNotetakerTimingPhase;
  event: string;
  timestamp: string;
  monotonicMs?: number;
  durationMs?: number;
  status?: 'started' | 'finished' | 'failed';

  appointmentId?: string;
  encounterId?: string;
  questionnaireResponseId?: string;
  documentReferenceId?: string;
  aiRunId?: string;

  transcriptBytes?: number;
  transcriptChars?: number;
  transcriptWordCountApprox?: number;

  model?: string;
  inputTokens?: number;
  outputTokens?: number;

  errorCode?: string;
  errorMessage?: string;

  /** Optional scenario label for grouping and filtering (never PHI). */
  scenario?: string;
};

// ---------------------------------------------------------------------------
// Config
// ---------------------------------------------------------------------------

function isEnabled(): boolean {
  try {
    return import.meta.env.VITE_AI_NOTETAKER_TIMING === 'true';
  } catch {
    return false;
  }
}

// ---------------------------------------------------------------------------
// Trace ID helpers
// ---------------------------------------------------------------------------

let runCounter = 0;

function shortId(): string {
  return `${Date.now().toString(36)}-${(++runCounter).toString(36)}`;
}

export function createAiGenerationTraceId(
  appointmentId: string,
  questionnaireResponseId?: string,
  aiRunId?: string,
): string {
  const runId = aiRunId || shortId();
  return `ai-notetaker:ai-generation:${appointmentId}:${questionnaireResponseId || 'unknown'}:${runId}`;
}

// ---------------------------------------------------------------------------
// Global event store
// ---------------------------------------------------------------------------

const STORAGE_KEY = 'aiNotetakerTimingEvents';
const WINDOW_KEY = '__AI_NOTETAKER_TIMING_EVENTS__';
const MAX_EVENTS = 500;

declare global {
  interface Window {
    [WINDOW_KEY]?: AiNotetakerTimingEvent[];
  }
}

let hydratedFromStorage = false;

function hydrateFromLocalStorage(): void {
  if (hydratedFromStorage || typeof window === 'undefined') return;
  hydratedFromStorage = true;
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return;
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed) || parsed.length === 0) return;
    const store = getEventStore();
    if (store.length === 0) {
      store.push(...(parsed as AiNotetakerTimingEvent[]).slice(-MAX_EVENTS));
    }
  } catch {
    // ignore corrupt or unavailable storage
  }
}

function getEventStore(): AiNotetakerTimingEvent[] {
  if (typeof window === 'undefined') return [];
  if (!window[WINDOW_KEY]) {
    window[WINDOW_KEY] = [];
  }
  return window[WINDOW_KEY]!;
}

function persistToLocalStorage(events: AiNotetakerTimingEvent[]): void {
  try {
    const trimmed = events.slice(-MAX_EVENTS);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(trimmed));
  } catch {
    // localStorage may be unavailable or full
  }
}

// ---------------------------------------------------------------------------
// Emitter
// ---------------------------------------------------------------------------

export function emitTimingEvent(event: AiNotetakerTimingEvent): void {
  if (!isEnabled()) return;

  console.log(formatConsoleLine(event));

  const store = getEventStore();
  store.push(event);
  if (store.length > MAX_EVENTS) {
    store.splice(0, store.length - MAX_EVENTS);
  }

  persistToLocalStorage(store);

  // Notify any listeners (e.g. the timing panel) so they can react without polling.
  try {
    window.dispatchEvent(new CustomEvent('ai-notetaker-timing-event', { detail: event }));
  } catch {
    // Not in a browser context (e.g. SSR test environments) — skip.
  }
}

// ---------------------------------------------------------------------------
// Convenience: checkpoint recorder
// ---------------------------------------------------------------------------

export class TimingRecorder {
  readonly traceId: string;
  readonly phase: AiNotetakerTimingPhase;
  private readonly baseFields: Partial<AiNotetakerTimingEvent>;

  constructor(opts: {
    traceId: string;
    phase: AiNotetakerTimingPhase;
    appointmentId?: string;
    encounterId?: string;
    aiRunId?: string;
  }) {
    this.traceId = opts.traceId;
    this.phase = opts.phase;
    this.baseFields = {
      appointmentId: opts.appointmentId,
      encounterId: opts.encounterId,
      aiRunId: opts.aiRunId,
    };
  }

  checkpoint(
    event: string,
    extra?: Partial<AiNotetakerTimingEvent>,
  ): void {
    if (!isEnabled()) return;
    emitTimingEvent({
      traceId: this.traceId,
      phase: this.phase,
      event,
      timestamp: new Date().toISOString(),
      monotonicMs: performance.now(),
      ...this.baseFields,
      ...extra,
    });
  }

  started(event: string, extra?: Partial<AiNotetakerTimingEvent>): void {
    this.checkpoint(event, { status: 'started', ...extra });
  }

  finished(
    event: string,
    startedAtMs: number,
    extra?: Partial<AiNotetakerTimingEvent>,
  ): void {
    const now = performance.now();
    this.checkpoint(event, {
      status: 'finished',
      durationMs: roundDurationMs(now - startedAtMs),
      ...extra,
    });
  }

  failed(
    event: string,
    error: unknown,
    extra?: Partial<AiNotetakerTimingEvent>,
  ): void {
    const msg = error instanceof Error ? error.message : String(error);
    this.checkpoint(event, {
      status: 'failed',
      errorMessage: msg.slice(0, 200),
      ...extra,
    });
  }

  now(): number {
    return performance.now();
  }
}

export function createTimingRecorder(opts: {
  traceId: string;
  phase: AiNotetakerTimingPhase;
  appointmentId?: string;
  encounterId?: string;
  aiRunId?: string;
}): TimingRecorder | null {
  if (!isEnabled()) return null;
  return new TimingRecorder(opts);
}

// ---------------------------------------------------------------------------
// Utilities for reading stored events
// ---------------------------------------------------------------------------

export function getStoredTimingEvents(): AiNotetakerTimingEvent[] {
  hydrateFromLocalStorage();
  return getEventStore();
}

export function clearStoredTimingEvents(): void {
  const store = getEventStore();
  store.length = 0;
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    // ignore
  }
}
